import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Star,
  Check,
} from "lucide-react";
import { MarketingLayout } from "@/components/MarketingLayout";
import { Button } from "@/components/ui/button";
import { ViolationBadge } from "@/components/audit/ViolationBadge";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

type Violation = { type: string; confidence: number };

type Review = {
  id: string;
  rating: number;
  title: string | null;
  body: string;
  reviewerName: string | null;
  verifiedPurchase: boolean;
  classification: {
    flaggedForTakedown: boolean;
    violations: Violation[];
    rationale: string | null;
  };
};

type Result = {
  asin: string;
  reviewCount: number;
  flaggedCount: number;
  reviews: Review[];
  scannedCount?: number;
};

export default function ReviewChecker() {
  const [asin, setAsin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setResult(null);
    setSubmitting(true);

    const trimmed = asin.trim();
    // Accept either a full Amazon URL or a bare 10-char ASIN. The API
    // extracts the ASIN from URLs server-side via parseAsins().
    const asinMatch = trimmed.match(/\b(B0[A-Z0-9]{8}|[0-9]{9}[0-9X])\b/);
    if (!asinMatch) {
      setError(
        "Paste an Amazon product link (e.g. https://www.amazon.com/dp/B0CFPJYX7P) or the 10-character product code."
      );
      setSubmitting(false);
      return;
    }

    const res = await api<Result>("/instant-audit", {
      method: "POST",
      body: JSON.stringify({ asin: trimmed }),
    });

    setSubmitting(false);
    if (!res.ok || !res.data) {
      setError(res.error ?? "Couldn't check that ASIN. Try again in a moment.");
      return;
    }

    setResult(res.data);
    track("review_checker_run", {
      asin: res.data.asin,
      review_count: res.data.reviewCount,
      flagged_count: res.data.flaggedCount,
    });
  }

  return (
    <MarketingLayout>
      <div className="mx-auto w-full max-w-3xl px-6 py-16">
        <div className="text-center">
          <div className="text-sm font-mono uppercase tracking-[0.2em] text-emerald-700">
            Free · No signup
          </div>
          <h1 className="mt-3 text-4xl font-semibold tracking-tight text-slate-900 md:text-5xl">
            Free Amazon review checker
          </h1>
          <p className="mt-4 text-base text-slate-600">
            Paste a link to your Amazon product. We&rsquo;ll pull recent 1–3★
            reviews and flag the ones that look removable under Amazon&rsquo;s
            policy.
          </p>
        </div>

        <form onSubmit={onSubmit} className="mt-10">
          <label
            htmlFor="asin-input"
            className="mb-2 block text-sm font-medium text-slate-700"
          >
            Amazon product link
          </label>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <input
              id="asin-input"
              type="text"
              value={asin}
              onChange={(e) => setAsin(e.target.value)}
              placeholder="https://www.amazon.com/dp/B0CFPJYX7P"
              autoComplete="off"
              spellCheck={false}
              className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            />
            <Button type="submit" size="lg" disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? "Checking…" : "Check reviews →"}
            </Button>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Tip: open your product on amazon.com, copy the URL from the address
            bar, and paste it here.
          </p>
        </form>

        {error && (
          <p
            className="mt-4 flex items-center gap-2 text-sm text-destructive"
            role="alert"
          >
            <AlertTriangle className="h-4 w-4" /> {error}
          </p>
        )}

        {submitting && !result && (
          <ProgressSteps asin={asin.trim().toUpperCase().match(/B0[A-Z0-9]{8}|[0-9]{9}[0-9X]/)?.[0] ?? null} />
        )}

        {result && <Results result={result} />}

        {!result && !submitting && (
          <ul className="mt-12 grid gap-3 text-sm text-slate-600 sm:grid-cols-3">
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-emerald-600">✓</span>
              <span>
                Scans the most recent reviews on the product page
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-emerald-600">✓</span>
              <span>
                Flags 10 violation types from Amazon&rsquo;s policy
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-emerald-600">✓</span>
              <span>Free — no signup, no credit card</span>
            </li>
          </ul>
        )}
      </div>
    </MarketingLayout>
  );
}

function Results({ result }: { result: Result }) {
  const flagged = result.reviews.filter((r) => r.classification.flaggedForTakedown);
  const clean = result.reviews.filter((r) => !r.classification.flaggedForTakedown);

  return (
    <div className="mt-12 space-y-8">
      <ResultCard
        asin={result.asin}
        flaggedCount={flagged.length}
        reviewCount={result.reviewCount}
        scannedCount={result.scannedCount ?? result.reviewCount}
      />

      {flagged.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
            Removable reviews
          </h2>
          <ul className="mt-4 space-y-3">
            {flagged.map((r) => (
              <ReviewRow key={r.id} review={r} />
            ))}
          </ul>
        </section>
      )}

      {clean.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
            Looks fine ({clean.length})
          </h2>
          <ul className="mt-4 space-y-2">
            {clean.map((r) => {
              const headline =
                r.title ||
                (r.body ? r.body.slice(0, 100) + (r.body.length > 100 ? "…" : "") : "");
              return (
                <li
                  key={r.id}
                  className="rounded-md border border-slate-100 bg-slate-50 px-4 py-3"
                >
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                    <StarRating rating={r.rating} />
                    {r.verifiedPurchase && (
                      <span className="text-emerald-700">Verified</span>
                    )}
                    {r.reviewerName && (
                      <span className="text-slate-500">· {r.reviewerName}</span>
                    )}
                  </div>
                  {headline ? (
                    <div className="mt-1 line-clamp-1 text-sm text-slate-800">
                      {headline}
                    </div>
                  ) : (
                    <div className="mt-1 text-sm italic text-slate-400">
                      (no title or body extracted)
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
}

function ReviewRow({ review }: { review: Review }) {
  return (
    <li className="rounded-xl border border-amber-200 bg-amber-50 p-5">
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
        <StarRating rating={review.rating} />
        {review.verifiedPurchase && (
          <>
            <span>·</span>
            <span className="text-emerald-700">Verified</span>
          </>
        )}
        {review.reviewerName && (
          <>
            <span>·</span>
            <span>{review.reviewerName}</span>
          </>
        )}
      </div>
      {review.title && (
        <div className="mt-1 font-medium text-slate-900">{review.title}</div>
      )}
      <p className="mt-1 line-clamp-3 text-sm text-slate-700">{review.body}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {review.classification.violations.map((v, i) => (
          <ViolationBadge key={i} type={v.type} />
        ))}
      </div>
      {review.classification.rationale && (
        <p className="mt-2 text-xs italic text-slate-600">
          {review.classification.rationale}
        </p>
      )}
    </li>
  );
}

function ResultCard({
  asin,
  flaggedCount,
  reviewCount,
  scannedCount,
}: {
  asin: string;
  flaggedCount: number;
  reviewCount: number;
  scannedCount: number;
}) {
  if (flaggedCount === 0) {
    const noLowRated = reviewCount === 0 && scannedCount > 0;
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 md:p-8">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-6 w-6 shrink-0 text-emerald-600" />
          <h2 className="text-2xl font-semibold tracking-tight text-emerald-900 md:text-3xl">
            {noLowRated
              ? "No low-rated reviews to check."
              : "The reviews we sampled look fine."}
          </h2>
        </div>
        <p className="mt-4 max-w-xl text-sm text-emerald-900 md:text-base">
          {noLowRated ? (
            <>
              We sampled {scannedCount} recent review{scannedCount === 1 ? "" : "s"} on{" "}
              <span className="font-mono text-emerald-950">{asin}</span> and they were
              all 4 or 5 stars — nothing worth challenging. Want a deeper scan, or
              alerts when new low-rated reviews come in?
            </>
          ) : (
            <>
              We checked the {reviewCount} most recent 1–3★ review
              {reviewCount === 1 ? "" : "s"} on{" "}
              <span className="font-mono text-emerald-950">{asin}</span> and didn&rsquo;t
              find anything that violates Amazon&rsquo;s policy. Want a more
              thorough scan, or alerts when new reviews look removable?
            </>
          )}
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Link
            to={`/audit?asin=${asin}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            Run a deeper audit →
          </Link>
          <Link
            to={`/checkout/subscribe?asin=${asin}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-sm font-medium text-slate-900 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"
          >
            Start monitoring — $149/mo
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-900 bg-slate-900 p-6 text-white md:p-8">
      <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">
        {flaggedCount} review{flaggedCount === 1 ? "" : "s"} may violate
        Amazon policy.
        <br />
        <span className="font-medium text-slate-300">
          We can help you prepare the next step.
        </span>
      </h2>
      <p className="mt-4 max-w-xl text-sm text-slate-300 md:text-base">
        We checked the {reviewCount} most recent 1–3★ review
        {reviewCount === 1 ? "" : "s"} on{" "}
        <span className="font-mono text-white">{asin}</span>. Reputely can
        keep watching the product, surface new policy violations, and prepare
        appeal-ready submissions.
      </p>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Link
          to={`/start-trial?asin=${asin}`}
          className="inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-100"
        >
          Start free trial →
        </Link>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-600"
        >
          Compare plans
        </Link>
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
        <a
          href="https://cal.com/maximiliang/demo"
          target="_blank"
          rel="noreferrer"
          className="text-slate-300 hover:text-white"
        >
          Book a call
        </a>
        <Link to="/pricing" className="text-slate-300 hover:text-white">
          See pricing
        </Link>
        <Link to="/how-it-works" className="text-slate-300 hover:text-white">
          How it works
        </Link>
      </div>
      <div className="mt-5 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-slate-400">
        <span className="inline-flex items-center gap-1.5">
          <span className="text-emerald-400">✓</span> We draft the takedown request
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="text-emerald-400">✓</span> Managed Brand Registry
          filing when access is verified
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="text-emerald-400">✓</span> Amazon makes the final
          removal decision
        </span>
      </div>
    </div>
  );
}

function StarRating({ rating }: { rating: number }) {
  const safe = Math.max(0, Math.min(5, Math.round(rating)));
  return (
    <span
      className="inline-flex items-center gap-0.5"
      aria-label={`${safe} out of 5 stars`}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`h-3.5 w-3.5 ${
            n <= safe
              ? "fill-amber-400 text-amber-400"
              : "fill-slate-200 text-slate-200"
          }`}
        />
      ))}
    </span>
  );
}

const PROGRESS_STEPS = [
  { label: "Opened Amazon product page", at: 0 },
  { label: "Pulled the most recent 1–3★ reviews", at: 5_000 },
  { label: "Checking each review for policy violations", at: 11_000 },
  { label: "Compiling results", at: 19_000 },
] as const;

function ProgressSteps({ asin }: { asin: string | null }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const id = window.setInterval(() => {
      setElapsed(Date.now() - start);
    }, 250);
    return () => window.clearInterval(id);
  }, []);

  // Active step = highest step whose `at` threshold has been crossed.
  let activeIdx = 0;
  for (let i = PROGRESS_STEPS.length - 1; i >= 0; i--) {
    if (elapsed >= PROGRESS_STEPS[i].at) {
      activeIdx = i;
      break;
    }
  }

  return (
    <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6">
      {asin && (
        <div className="mb-4 text-sm text-slate-700">
          Checking <span className="font-mono">{asin}</span>
        </div>
      )}
      <ul className="space-y-3">
        {PROGRESS_STEPS.map((step, i) => {
          const done = i < activeIdx;
          const active = i === activeIdx;
          return (
            <li key={step.label} className="flex items-start gap-3 text-sm">
              <span
                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ring-2 ${
                  done
                    ? "bg-emerald-500 ring-emerald-500"
                    : active
                      ? "bg-blue-500 ring-blue-500"
                      : "bg-transparent ring-slate-300"
                }`}
                aria-hidden
              >
                {done && <Check className="h-3 w-3 text-white" />}
                {active && (
                  <Loader2 className="h-3 w-3 animate-spin text-white" />
                )}
              </span>
              <span
                className={
                  done
                    ? "text-slate-500"
                    : active
                      ? "font-medium text-slate-900"
                      : "text-slate-400"
                }
              >
                {step.label}
                {active ? "…" : ""}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="mt-5 text-xs text-slate-500">
        Usually takes 15-30 seconds.
      </p>
    </div>
  );
}
